import {z} from "zod/v4-mini"
import type {Evaluator} from "@cm/graph-v3"
import {graphNode} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {NumberNode} from "#template/nodes/number/number-node"
import type {OnCompileContext, OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {StringLike} from "#template/nodes/types"
import {stringLike} from "#template/nodes/string/fundamental"
import {GeometryGraphPoint} from "#template/nodes/geometry-graph/output/geometry-graph-point"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {ReifiedPointData} from "#template/interfaces/point-data"
import type {ReifiedGeometryAttributeData} from "#template/interfaces/geometry-data"
import {elementCountAndTypeFromValueType} from "#template/geometry-processing/geometry-graph"
import {visitNone} from "#template/template-node/visit-helpers"

function parseScalarFromReifiedPointData(reifiedPointData: ReifiedPointData, scalarPath: string) {
    const pathParts = scalarPath.split("/")
    const pathPartsCount = pathParts.length

    let currentDataRef: ReifiedPointData["attributeSet"] = reifiedPointData.attributeSet

    const isReifiedPointDataAttribute = (data: ReifiedPointData["attributeSet"][string]): data is Omit<ReifiedGeometryAttributeData, "indexingDim"> => {
        return "valueType" in data && "data" in data && (data.data instanceof Float32Array || data.data instanceof Uint32Array)
    }

    const isReifiedPointDataAttributeSet = (data: ReifiedPointData["attributeSet"][string]): data is ReifiedPointData["attributeSet"] => {
        return !isReifiedPointDataAttribute(data)
    }

    for (let i = 0; i < pathPartsCount; i++) {
        const pathPart = pathParts[i]
        if (currentDataRef[pathPart] === undefined) return null
        if (i === pathPartsCount - 1) {
            if (!isReifiedPointDataAttribute(currentDataRef[pathPart]) || elementCountAndTypeFromValueType(currentDataRef[pathPart].valueType).count !== 1) {
                return null
            }
            return currentDataRef[pathPart].data[0]
        }

        if (!isReifiedPointDataAttributeSet(currentDataRef[pathPart])) {
            return null
        }

        currentDataRef = currentDataRef[pathPart]
    }

    return null
}

export type GeometryGraphPointScalarParameters = {
    name: string
    geometryGraphPoint: GeometryGraphPoint | null
    scalarPath: StringLike | null
}

const definition: TemplateNodeDefinition<GeometryGraphPointScalarParameters> = {
    nodeClass: "GeometryGraphPointScalar",
    validation: {
        parameters: z.object({
            name: z.string(),
            geometryGraphPoint: z.nullable(graphNode(GeometryGraphPoint)),
            scalarPath: z.nullable(stringLike),
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class GeometryGraphPointScalar extends NumberNode<GeometryGraphPointScalarParameters> {
    public override get definition() {
        return definition
    }

    public override onFilterActive(
        _context: OnFilterActiveContext,
        _evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<GeometryGraphPointScalarParameters> {
        if (this.parameters.geometryGraphPoint === null || this.parameters.scalarPath === null) return skipped
        return visitNone(this.parameters)
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<GeometryGraphPointScalarParameters> {
        const {geometryGraphPoint, scalarPath} = this.parameters
        if (geometryGraphPoint === null || scalarPath === null) return skipped

        const {evaluator} = context
        const {templateScope} = evaluator

        const scope = evaluator.getScope(this)

        const [evaluatedGeometryGraphPointDataValid, evaluatedGeometryGraphPointDataInvalid] = scope.branch(evaluator.evaluate(geometryGraphPoint))
        const [evaluatedPathValid, evaluatedPathInvalid] = scope.branch(evaluator.evaluate(scalarPath))

        const evaluatedReifiedPointData = scope.get(evaluatedGeometryGraphPointDataValid, "reified")

        const [parsedScalarValid, parsedScalarInvalid] = scope.branch(
            scope.phi(
                scope.pureLambda(
                    scope.tuple(evaluatedReifiedPointData, evaluatedPathValid),
                    ([reifiedPointData, scalarPath]) => parseScalarFromReifiedPointData(reifiedPointData, scalarPath),
                    "parseScalarFromReifiedPointData",
                ),
                evaluatedGeometryGraphPointDataInvalid,
                evaluatedPathInvalid,
            ),
        )

        templateScope.alias(scope.phi(parsedScalarValid, parsedScalarInvalid), `geometryGraphPointScalar-${evaluator.getLocalId(this)}`)

        return visitNone(this.parameters)
    }

    public override evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<number | null> {
        return scope.unresolvedToNull(evaluator.templateScope.resolve<number | null>(`geometryGraphPointScalar-${evaluator.getLocalId(this)}`))
    }
}
